// One formatter for "which site is this?" across every surface that shows a
// job's location: `#{number} · {city}`.
//
// ⚠️ Keep this the single source. The three list pages read the same pair from
// three DIFFERENT payload shapes — a job carries property_number/property_city,
// a visit carries them on its light `job` summary, a PO carries them flattened
// as job_property_number/job_city — so the temptation is a copy per registry,
// and they then drift. The jobs column did exactly that: it rendered off the
// full `costco_site` object as `#number · name · city`, which prints the city
// twice on most sites (site #1 is name "Seattle", city "Seattle").
//
// Reading property_number rather than costco_site.site_number is safe and is
// also what the Property *filter* and the server-side `q` search match on:
// verified against the live DB — every Costco-linked job has property_number
// set, and it equals costco_site.site_number on all of them.
export function formatProperty(number, city) {
  return [number ? `#${number}` : null, city].filter(Boolean).join(" · ");
}

/**
 * "#348 Jurupa Valley, CA" — the property number directly in front of a place
 * name (owner ask, Sep 2026: "that's the convention they're used to"), NOT
 * as its own comma/middot-separated segment the way `formatProperty`'s
 * Property-column rendering is.
 *
 * ⚠️ Prefers the Costco site's own (narrower) `name` over `property_city`
 * (owner ask, Sep 28, 2026 — same reasoning as `CalendarPage.jsx`'s
 * `jobLocationLabel`, which already does this for visit pills/panels: a
 * site's name is often a neighborhood/nickname — "Wesley Chapel" — that
 * disagrees with its postal city ("Lutz, FL"), and the name is the more
 * useful, narrower identifier for a job title. `property_city` is NOT a
 * reliable "real city" anyway — it's regex-parsed off BuildOps' own property
 * label for migrated jobs, and copied from the picked property's real
 * city/state for portal-created jobs, so the two sources already disagree on
 * which of these two concepts it holds. If a genuine postal city is ever
 * needed, read it off the address instead of this field.
 *
 * Checks both shapes a job/visit payload can carry: the FULL job serializer's
 * nested `costco_site.name`, and a light summary's flat `costco_site_name`
 * (mirroring `_job_summary()`'s field in `events_service.py`). Neither present
 * (a light payload with no Costco data, or a non-Costco job) falls back to
 * `property_city`, unchanged from before.
 */
export function jobPlaceLabel(job) {
  if (!job) return null;
  const place = job.costco_site_name || job.costco_site?.name || job.property_city;
  return [job.property_number ? `#${job.property_number}` : null, place]
    .filter(Boolean)
    .join(" · ") || null;
}

/**
 * A job's full customer/place/number identity, e.g.
 * "Costco Wholesale · #348 Jurupa Valley, CA · Job #3000000019".
 * The job number is labelled "Job #" (from Fenario's job UI overhaul) so it
 * doesn't read as a second property number; the place keeps the Portal's
 * "#348 Jurupa Valley" convention. One implementation on purpose — every job
 * title in the app comes through here.
 */
export function jobTitle(job) {
  if (!job) return "";
  return (
    [job.customer_name, jobPlaceLabel(job), job.job_number ? `Job\u00a0#${job.job_number}` : null]
      .filter(Boolean)
      .join(" · ") ||
    job.name ||
    (job.id != null ? `Job ${job.id}` : "Job")
  );
}

export function visitJobLabel(v) {
  return jobTitle(v?.job) || `Job ${v?.job_id}`;
}
